import React, { useState, useEffect, Fragment } from 'react'
import Layout from '../modules/Layout'
import { useTranslation } from 'react-i18next'
import Image from '../atoms/Image'
import Link from '../atoms/Link'
import Richtext from 'gatsby-theme-kn/src/components/atoms/Richtext'
import OneColWrapper from 'gatsby-theme-kn/src/components/technical/OneColWrapper'
import SEO from '../modules/Seo'
import DealerSearchBlock from '../blocks/DealerSearchBlock'
import ListboxFilterItem from '../atoms/ListboxFilterItem'
import { useLocation } from '@reach/router'
import queryString from 'query-string'
import SelectOrder from '../atoms/SelectOrder'

const ProductCategoryPage = ({
  locale,
  content,
  name,
  products,
  alternates,
}) => {
  const { t } = useTranslation()
  const [searchIn, setSearchIn] = useState('')
  const [searchFor, setSearchFor] = useState('')
  useEffect(() => {
    setSearchIn(`${t('product_category_page.searching_in')}`)
    setSearchFor(`${t('product_category_page.searching_for')}`)
  }, [locale])

  const defaultOptions = [
    {
      label: t('product_category_page.recommendation'),
      type: 'recommendation',
      order: 'asc',
    },
    { label: 'A-Z', type: 'alphabetical', order: 'asc' },
    { label: t('product_category_page.newest'), type: 'newest', order: 'asc' },
  ]

  const productsWithRecommendation = products.map((product, i) => ({
    ...product,
    recommendation: i,
  }))

  const manualFilter = {
    title: `${content.collection_links?.[0].title}`,
    slug: content.collection_links?.[0].slug,
    tags: content.collection_links
      ? content.collection_links
          .slice(1)
          .map((link) => ({ slug: link.slug, title: link.title }))
      : [],
  }
  useEffect(() => {
    return () => {
      setAllProducts([...products])
    }
  }, [locale])

  const [filters, setFilters] = useState(createFilters(products, manualFilter))

  const [selectedFilter, setSelectedFilter] = useState(
    getInitialStateFromURL('filter')
  )
  const [sortOption, setSortOption] = useState(
    getInitialStateFromURL('sorting')
  )
  const [allProducts, setAllProducts] = useState([
    ...productsWithRecommendation,
  ])
  const [filteredProducts, setFilteredProducts] = useState([
    ...productsWithRecommendation,
  ])
  const [filteredProductTags, setFilteredProductTags] = useState([])

  const [allVisuals, setAllVisuals] = useState(
    createVisualProperties(content.visuals)
  )
  const [activeVisual, setActiveVisual] = useState(
    initialActiveVisuals(content.visuals)
  )
  function initialActiveVisuals(visuals) {
    const activeStyle = visuals?.styles?.find((item) => item.default)
    const activeView = visuals?.views?.find((item) => item.default)
    if (!activeStyle || !activeView) return {}
    return {
      styles: {
        id: activeStyle?.id,
        label: activeStyle?.title,
        type: activeStyle?.title,
      },
      views: {
        id: activeView?.id,
        label: activeView?.title,
        type: activeView?.title,
      },
    }
  }

  function createFilters(products, manualFilter) {
    const filtersData = []
    /* if (manualFilter) {
      const formattedManualFilter = {
        ...manualFilter,
        tags: manualFilter.tags?.map((tag) => ({ ...tag })) || [],
        position: 0,
      }
      filtersData.push(formattedManualFilter)
    } */
    products.forEach((product) => {
      product.content.tag_lists?.forEach((tagList) => {
        let existingTagList = filtersData.find((t) => t.slug === tagList.slug)
        if (existingTagList) {
          existingTagList.tags = Array.from(
            new Set([
              ...existingTagList.tags,
              ...tagList.tags.map((tag) => ({ ...tag })),
            ])
          )
        } else {
          filtersData.push({
            ...tagList,
            tags: tagList.tags.map((tag) => ({ ...tag })),
          })
        }
      })
    })
    const filters = filtersData
      .map((filter) => ({
        title: filter.title,
        slug: filter.slug,
        position: filter.position,
        tags: filter.tags
          .reduce((uniqueTags, tag) => {
            if (!uniqueTags.some((uniqueTag) => uniqueTag.slug === tag.slug)) {
              uniqueTags.push(tag)
            }
            return uniqueTags
          }, [])
          .sort((a, b) => a.position - b.position), // Sorting tags by position (ascending)
      }))
      .sort((a, b) => a.position - b.position)
    return filters
  }

  function createVisualProperties(visuals) {
    const newStyles = visuals?.styles?.map((visual) => {
      return {
        id: visual.id,
        label: visual.title,
        active: visual.default,
        type: visual.title,
      }
    })
    const newViews = visuals?.views?.map((view) => {
      return {
        id: view.id,
        label: view.title,
        active: view.default,
        type: view.title,
      }
    })
    return { styles: newStyles, views: newViews }
  }

  function getInitialStateFromURL(type) {
    const location = useLocation()
    const queryParams = queryString.parse(location.search)

    if (type === 'filter') {
      const filters = Object.keys(queryParams)
        .filter((key) => key !== 'sort')
        .map((category) => ({
          category,
          filters: queryParams[category].split(',').map(decodeURIComponent),
        }))
      return filters
    }

    if (type === 'sorting') {
      const defaultSorting = { type: 'recommendation', order: 'desc' }
      const sortParam = queryParams.sort ? queryParams.sort.split(',') : null

      if (sortParam && sortParam[0] !== 'recommendation') {
        return { type: sortParam[0], order: sortParam[1] }
      } else {
        return defaultSorting
      }
    }
  }

  function updateFilters(category, filters) {
    setSelectedFilter((prevSelectedFilter) => {
      const categoryIndex = prevSelectedFilter.findIndex(
        (item) => item.category === category
      )
      if (categoryIndex !== -1) {
        const updatedFilters = prevSelectedFilter
          .map((item, index) =>
            index === categoryIndex ? { ...item, filters } : item
          )
          .filter((item) => item.filters.length > 0)
        return updatedFilters
      } else {
        if (filters.length > 0) {
          return [...prevSelectedFilter, { category, filters }]
        } else {
          return prevSelectedFilter
        }
      }
    })
  }

  function filterProducts(productsToFilter, selectedFilters) {
    return productsToFilter.filter((product) => {
      return selectedFilters.every((filterCategory) => {
        if (filterCategory.filters.length === 0) return true
        const productTagList = product.content.tag_lists?.find(
          (tagList) => tagList.slug === filterCategory.category
        )
        /* const productSubcategories = product.content.sub_categories.data
        if (!productTagList) {
          if (productSubcategories) {
            return filterCategory.filters.some((filter) =>
              productSubcategories.some(
                (productCategory) => productCategory.slug === filter
              )
            )
          }
          return false
        } */
        return filterCategory.filters.some((filter) =>
          productTagList?.tags.some((productTag) => productTag.slug === filter)
        )
      })
    })
  }

  function updateSorting(newSortOption) {
    setSortOption(newSortOption)
  }

  function updateActiveStyleVisual(visual) {
    setActiveVisual((prev) => ({ ...prev, styles: visual }))
  }

  function updateActiveViewVisual(visual) {
    setActiveVisual((prev) => ({ ...prev, views: visual }))
  }

  function sortProducts(products, option) {
    function sortAlphabetically(products) {
      return [...products].sort((a, b) => a.name.localeCompare(b.name))
    }
    function sortByRecommendation(products) {
      return [...products].sort((a, b) => a.recommendation - b.recommendation)
    }
    function sortByDate(products) {
      return [...products].sort((a, b) => {
        const dateA = new Date(a.content.created_at)
        const dateB = new Date(b.content.created_at)
        return dateB - dateA
      })
    }

    const { type } = option

    const sortedProducts =
      type === 'alphabetical'
        ? sortAlphabetically(products)
        : type === 'newest'
        ? sortByDate(products)
        : sortByRecommendation(products)

    return sortedProducts
  }

  function resetFilterAndSorting() {
    setSelectedFilter([])
    setSortOption({
      label: 'Empfohlen',
      type: 'recommendation',
    })
  }

  function generateFilteredProductsTags(products, mainCategorySlug) {
    const tagsResult = {}
    tagsResult[mainCategorySlug] = []

    products.forEach((product) => {
      if (product.content.tag_lists) {
        product.content.tag_lists.forEach((tagList) => {
          if (!tagsResult[tagList.slug]) {
            tagsResult[tagList.slug] = []
          }
          tagList.tags.forEach((tag) => {
            if (!tagsResult[tagList.slug].includes(tag.slug)) {
              tagsResult[tagList.slug].push(tag.slug)
            }
          })
        })
      }
      /*  if (
        product.content.sub_categories &&
        product.content.sub_categories.data
      ) {
        product.content.sub_categories.data.forEach((subcategory) => {
          if (!tagsResult[mainCategorySlug].includes(subcategory.slug)) {
            tagsResult[mainCategorySlug].push(subcategory.slug)
          }
        })
      } */
    })

    return tagsResult
  }

  function displayCorrectImage(item, viewsID, stylesID) {
    if (!item.content.visual_uploads) {
      return item.content.isolated_image
    }

    const matchingImage = item.content.visual_uploads.find(
      (upload) => upload.view_id === viewsID && upload.style_id === stylesID
    )

    if (
      matchingImage?.upload?.original_url &&
      matchingImage?.upload?.original_url !== ''
    ) {
      return matchingImage.upload.original_url
    } else {
      return item.content.isolated_image
    }
  }

  useEffect(() => {
    const filterParams = selectedFilter
      .map((filter) => {
        const filtersJoined = filter.filters.join(',')
        return `${encodeURIComponent(filter.category)}=${encodeURIComponent(
          filtersJoined
        )}`
      })
      .join('&')
    const filtered = filterProducts(allProducts, selectedFilter)

    const sortedAndFiltered = sortProducts(filtered, sortOption)
    const productTags = generateFilteredProductsTags(
      sortedAndFiltered,
      content.collection_links?.[0].slug
    )
    setFilteredProductTags(productTags)
    setFilteredProducts(sortedAndFiltered)

    let queryParams = filterParams
    if (sortOption.type !== 'recommendation') {
      const sortParam = `sort=${encodeURIComponent(sortOption.type)}`
      queryParams = queryParams ? `${queryParams}&${sortParam}` : sortParam
    }

    const newUrl = `${window.location.pathname}?${queryParams}`
    window.history.replaceState({}, '', newUrl)
  }, [selectedFilter, sortOption])

  useEffect(() => {
    function getFiltersFromQueryParams() {
      const queryParams = new URLSearchParams(window.location.search)
      const filters = []
      queryParams.forEach((value, key) => {
        if (key !== 'sort') {
          filters.push({
            category: key,
            filters: value.split(',').map(decodeURIComponent),
          })
        }
      })
      return filters
    }

    function getSortOptionFromQueryParams() {
      const queryParams = new URLSearchParams(window.location.search)
      const sortParam = queryParams.get('sort')
      if (sortParam && sortParam !== 'recommendation') {
        return { type: sortParam, order: 'desc' }
      } else {
        return { type: 'recommendation', order: 'desc' }
      }
    }
    const initialFilters = getFiltersFromQueryParams()
    const initialSortOption = getSortOptionFromQueryParams()

    setSelectedFilter(initialFilters)
    setSortOption(initialSortOption)
  }, [])

  useEffect(() => {
    setAllVisuals(createVisualProperties(content.visuals))
  }, [content.visuals])

  return (
    <Layout locale={locale} alternates={alternates}>
      <SEO content={content} name={name} og_locale={locale} />
      <section className="skewed-block skewed-block-on-gray">
        <div className="skewed-block-space"></div>
      </section>

      <section className="product-overview-block">
        <div className="product-overview-block-container">
          <div className="product-overview-block-wrapper">
            <div className="product-overview-block-header">
              <h1>{content.collection_links?.[0].title}</h1>
            </div>{' '}
            <div className="product-overview-block-content">
              <div className="product-overview-block-top">
                <div className="product-overview-block-top-filter">
                  {activeVisual.styles && true == false && (
                    <div className="product-overview-block-top-filter-item">
                      <SelectOrder
                        updateSorting={updateActiveStyleVisual}
                        activeSorting={activeVisual.styles}
                        options={allVisuals.styles}
                      />
                    </div>
                  )}
                  {activeVisual.views && true == false && (
                    <div className="product-overview-block-top-filter-item">
                      <SelectOrder
                        updateSorting={updateActiveViewVisual}
                        activeSorting={activeVisual.views}
                        options={allVisuals.views}
                      />
                    </div>
                  )}
                  <div className="product-overview-block-top-filter-item">
                    <SelectOrder
                      updateSorting={updateSorting}
                      activeSorting={sortOption}
                      options={defaultOptions}
                    />
                  </div>
                </div>
              </div>
              <div className="product-overview-block-sidebar">
                <div className="product-overview-block-sidebar-bg">
                  <div className="product-overview-block-sidebar-filter">
                    <div className="product-overview-block-sidebar-filter-item">
                      <button
                        className="btn btn-reset"
                        onClick={() => resetFilterAndSorting()}
                      >
                        {t('product_category_page.all')}
                      </button>
                    </div>
                    {filters.map((item, index) => (
                      <ListboxFilterItem
                        updateFilters={updateFilters}
                        key={item.title + index}
                        item={item}
                        filteredProductTags={filteredProductTags}
                        selectedFilter={selectedFilter}
                        productAmount={filteredProducts.length}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div
                className="product-overview-block-grid"
                id="product-filter-wrapper"
              >
                <div className="product-overview-block-grid-items">
                  {filteredProducts?.length > 0 &&
                    filteredProducts.map((item, index) => (
                      <div
                        key={item.full_slug + index}
                        className="product-overview-block-grid-item"
                      >
                        <Link
                          link={`/${item.full_slug}`}
                          className="product-overview-block-grid-item-content"
                        >
                          <div className="product-overview-block-grid-item-image">
                            <div
                              style={{
                                width: '100%',
                                height: 'auto',
                                aspectRatio: '1/1',
                              }}
                            >
                              <Image
                                index={index}
                                className="img-fluid"
                                image={displayCorrectImage(
                                  item,
                                  activeVisual?.views?.id,
                                  activeVisual?.styles?.id
                                )}
                                imageWidth="255"
                                imageHeight="255"
                                fillColor="FFFFFF"
                                alt={`${item.name} - ${item.content.headline}`}
                              />
                            </div>
                          </div>
                          <div className="product-overview-block-grid-item-text">
                            <h4>
                              <span>{item.name}</span>
                            </h4>
                            <p>{item.content.headline}</p>
                          </div>
                          {item.content.uploads?.main_award?.length > 0 && (
                            <div className="product-overview-block-grid-item-eyecatcher">
                              <div
                                className="product-overview-block-grid-item-eyecatcher-content"
                                style={{
                                  backgroundImage: `url(${item.content.uploads?.main_award[0].preview_url})`,
                                }}
                              >
                                <span>{item.content.title}</span>
                              </div>
                            </div>
                          )}
                        </Link>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section
        className="product-category-description-block"
        style={{ background: '#f6f6f6' }}
      >
        <OneColWrapper block={{}}>
          <Richtext text={content.product_category_description} />
        </OneColWrapper>
      </section>
      <DealerSearchBlock block={{}} />
    </Layout>
  )
}

export default ProductCategoryPage
